/**
 * useBadgeToaster — polls /api/badges/evaluate every 30 s (and once on mount)
 * to surface newly earned badges as a "celebration" toast.
 *
 * The initial call is used ONLY to prime the "already known" set — its result
 * is dropped so users who just logged in with a rich seeded state don't get a
 * cascade of toasts. Subsequent calls toast whatever new badge got awarded
 * since login.
 */
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

let __primed = false;
let __catalog = null;

async function ensureCatalog() {
    if (__catalog) return __catalog;
    try {
        const { data } = await api.get("/badges");
        __catalog = {};
        for (const b of data.badges || []) {
            __catalog[b.code] = b;
        }
    } catch {
        __catalog = {};
    }
    return __catalog;
}

export function useBadgeToaster() {
    const cancelled = useRef(false);

    useEffect(() => {
        cancelled.current = false;
        let iv = null;

        async function tick(isFirst) {
            try {
                const { data } = await api.post("/badges/evaluate");
                if (cancelled.current) return;
                const newly = data?.newly_earned || [];
                if (!isFirst && newly.length > 0) {
                    const catalog = await ensureCatalog();
                    for (const code of newly) {
                        const meta = catalog[code];
                        toast.success(
                            meta ? `🏆 ${meta.title_fr}` : "Nouveau succès débloqué",
                            {
                                description: meta?.description_fr || "Rendez-vous dans Progression → Succès pour l'admirer.",
                                duration: 6000,
                            },
                        );
                    }
                }
            } catch {
                // Silent — background poll.
            }
        }

        // First-run: prime and discard toasts.
        (async () => {
            await ensureCatalog();
            if (!__primed) {
                await tick(true);
                __primed = true;
            } else {
                // On subsequent mounts (e.g. route change), continue toasting.
                await tick(false);
            }
            if (cancelled.current) return;
            iv = setInterval(() => tick(false), 30000);
        })();

        return () => {
            cancelled.current = true;
            if (iv) clearInterval(iv);
        };
    }, []);
}
